import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../api/apiService';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Key } from 'lucide-react';
import './Profile.css';

export const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { t } = useTranslation();
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsLoading(true);

    try {
      const updatedUser = await apiService.updateProfile({ name });
      updateUser(updatedUser);
      setMessage(t('profileUpdated'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setIsLoading(true);

    try {
      await apiService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setMessage(t('passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsPasswordDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-page">
      {/* Profile Header Card */}
      <Card className="profile-header-card">
        <div className="profile-header-content">
          <div className="profile-avatar">
            <div className="avatar-circle">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
          </div>
          <div className="profile-header-info">
            <h1 className="profile-name">{user?.name}</h1>
            <p className="profile-email">{user?.email}</p>
            <span className="profile-role-badge">{t(user?.role || '')}</span>
          </div>
        </div>
      </Card>

      {/* Account Information Card */}
      <Card className="account-info-card">
        <h2 className="account-info-title">{t('accountInformation')}</h2>
        <p className="account-info-subtitle">{t('accountDetails')}</p>

        <div className="account-info-grid">
          <div className="account-info-item">
            <span className="account-info-label">{t('accountType')}</span>
            <span className="account-info-value">{t(user?.role || '')}</span>
          </div>
          <div className="account-info-item">
            <span className="account-info-label">{t('email')}</span>
            <span className="account-info-value">{user?.email}</span>
          </div>
          <div className="account-info-item">
            <span className="account-info-label">{t('status')}</span>
            <span className="status-badge active">{t('active')}</span>
          </div>
          <div className="account-info-item">
            <span className="account-info-label">{t('memberSince')}</span>
            <span className="account-info-value">{user?.created_at ? new Date(user.created_at).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }) : '-'}</span>
          </div>
        </div>
      </Card>

      {/* Update Profile Form */}
      <Card>
        <h2 className="section-title">{t('updateProfile')}</h2>
        <form onSubmit={handleUpdateProfile} className="profile-form">
          <Input
            label={t('email')}
            value={user?.email || ''}
            disabled
          />
          <Input
            label={t('name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            label={t('role')}
            value={t(user?.role || '')}
            disabled
          />
          <div className="profile-actions">
            <Button type="submit" disabled={isLoading}>
              {t('save')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsPasswordDialogOpen(true)}
            >
              <Key size={18} />
              {t('changePassword')}
            </Button>
          </div>
        </form>
      </Card>

      {/* Change Password Dialog */}
      <Dialog
        isOpen={isPasswordDialogOpen}
        onClose={() => {
          setIsPasswordDialogOpen(false);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setError('');
        }}
        title={t('changePassword')}
      >
        <form onSubmit={handleChangePassword} className="profile-form">
          <Input
            type="password"
            label={t('currentPassword')}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
          <Input
            type="password"
            label={t('newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <Input
            type="password"
            label={t('confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {error && <div className="error-message">{error}</div>}
          <Button type="submit" disabled={isLoading} className="w-full">
            {t('changePassword')}
          </Button>
        </form>
      </Dialog>

      {message && <div className="success-message">{message}</div>}
    </div>
  );
};
